import React, { useEffect, useState } from 'react';

import SectionVisibilityPanel from '../../event_setup/SectionVisibilityPanel';

import StepShell from './StepShell';

import ToggleInput from 'CommonComponents/ToggleInput';

import { useToast } from 'Hooks/useToast';

/**
 * Modules. Only the modules in the organisation's subscription plan are
 * offered, and a locked card (Budgeting) is always on — the server drops it
 * from the write set regardless of what is submitted. A card that cannot move
 * arrives with its reason, shown on the card; the save refuses the same
 * change with the same sentence.
 *
 * The Project Info visibility toggles below the cards edit the same setting
 * as Settings > Visible sections and save on their own, one at a time.
 */
export default function ModulesStep({ step, urls, deferring, saving, onDefer, onNext, onSaved }) {
    const [cards, setCards] = useState(null);
    const [canToggle, setCanToggle] = useState(false);
    const [sectionVisibility, setSectionVisibility] = useState(null);
    const [savingModules, setSavingModules] = useState(false);
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            .then(({ data }) => {
                setCards(data.modules);
                setCanToggle(data.can_toggle);
                setSectionVisibility(data.section_visibility ? { sections: data.section_visibility, updateUrl: data.section_visibility_url } : null);
            })
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    const toggle = (key) => {
        setCards((current) => current.map((card) => (card.key === key ? { ...card, enabled: !card.enabled } : card)));
    };

    const handleSave = () => {
        setSavingModules(true);

        const payload = (cards ?? []).filter((card) => !card.locked).reduce((body, card) => ({ ...body, [card.key]: card.enabled }), {});

        axios
            .put(urls.update, { modules: payload })
            .then(({ data }) => onSaved(data))
            .catch((e) => {
                // No Formik form here to hand the errors to, so a validation
                // failure is surfaced as its own message rather than the
                // generic one.
                const errors = e.response?.data?.errors;
                toast.error(errors ? Object.values(errors).flat().join(' ') : $t('Something went wrong. Please try again.'));
            })
            .finally(() => setSavingModules(false));
    };

    if (!cards) {
        return (
            <StepShell step={step} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}} nextLabel={$t('Save and continue')}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    const readOnly = step.read_only || !canToggle;

    return (
        <StepShell
            step={step}
            lede={$t('Choose what this project uses. Your choices decide which later setup pages appear; everything can be switched on later in Settings.')}
            deferring={deferring}
            saving={readOnly ? saving : savingModules}
            onDefer={onDefer}
            // Modules is a required step with no "Do this later", and its only
            // completion signal is saved_at — so a read-only user (no toggle
            // ability) still needs a way to advance. Falling back to the
            // shell's own step-saved marker, rather than the gated PUT this
            // user cannot make, matches the behaviour StepPlaceholder always
            // had here.
            onNext={readOnly ? onNext : handleSave}
            nextLabel={$t('Save and continue')}
        >
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                {cards.map((card) => (
                    <div key={card.key} className="tw-p-4 tw-rounded-lg tw-border tw-border-charcoal-15 tw-flex tw-flex-col tw-gap-y-2">
                        <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-4">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-charcoal tw-mb-0">{card.title}</h3>
                            <ToggleInput
                                noBottomSpacing={true}
                                // ToggleInput renders no visible label here, so the
                                // card title is the control's accessible name.
                                aria-label={card.title}
                                checked={card.enabled}
                                disabled={readOnly || card.locked || !!card.disabled_reason}
                                onChange={() => toggle(card.key)}
                                tooltip={card.locked ? $t('Budgeting is always on.') : card.disabled_reason || undefined}
                            />
                        </div>
                        <p className="tw-text-2xs tw-text-charcoal-50 tw-mb-0">{card.description}</p>
                        {card.disabled_reason && <p className="tw-text-2xs tw-font-medium tw-text-charcoal-75 tw-mb-0">{card.disabled_reason}</p>}
                    </div>
                ))}
            </div>

            {sectionVisibility && (
                <div className="tw-mt-6">
                    <SectionVisibilityPanel sections={sectionVisibility.sections} updateUrl={sectionVisibility.updateUrl} />
                </div>
            )}
        </StepShell>
    );
}
